import React, { createContext, useContext, useState, useCallback } from 'react';

// Create the context
const VariationContext = createContext();

/**
 * Provider component for variation context
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} VariationProvider component
 */
export function VariationProvider({ children }) {
  // UI state
  const [uiState, setUiState] = useState({
    showDeleteModal: false,
    isAudioOperation: false,
    uploadStatus: {
      loading: false,
      error: null,
      success: false
    }
  });

  // Set showDeleteModal
  const setShowDeleteModal = useCallback((showDeleteModal) => {
    setUiState(prevState => ({
      ...prevState,
      showDeleteModal
    }));
  }, []);

  // Set isAudioOperation
  const setIsAudioOperation = useCallback((isAudioOperation) => {
    setUiState(prevState => ({
      ...prevState,
      isAudioOperation
    }));
  }, []);

  // Set uploadStatus
  const setUploadStatus = useCallback((uploadStatus) => {
    setUiState(prevState => ({
      ...prevState,
      uploadStatus
    }));
  }, []);

  // Reset UI state
  const resetUiState = useCallback(() => {
    setUiState({
      showDeleteModal: false,
      isAudioOperation: false,
      uploadStatus: {
        loading: false,
        error: null,
        success: false
      }
    });
  }, []);

  // Context value
  const contextValue = {
    uiState,
    setShowDeleteModal,
    setIsAudioOperation,
    setUploadStatus,
    resetUiState
  };

  return (
    <VariationContext.Provider value={contextValue}>
      {children}
    </VariationContext.Provider>
  );
}

/**
 * Hook for using variation context
 * @returns {Object} Variation context
 */
export function useVariationContext() {
  const context = useContext(VariationContext);
  if (!context) {
    throw new Error('useVariationContext must be used within a VariationProvider');
  }
  return context;
}

export default VariationContext;
